import { useCallback, useRef, useState } from 'react';
import { findCameraWithTorch } from '../helpers';

export interface CapturePhotoOptions {
  // 'square' = ScanPlate behavior (center crop, max 1024); 'scale' = keep aspect, longest side
  // <= maxDimension; 'element' = crop the video to the on-screen rect of `cropElement` (mapped
  // through object-fit: cover) at full source resolution, no downscaling
  crop: 'square' | 'scale' | 'element';
  fileName: string;
  // crop 'element' only: the overlay element (e.g. the scan frame) whose visible area should be
  // captured; falls back to 'scale' when missing or when the source is not a live video
  cropElement?: HTMLElement | null;
  // 'square'/'scale' only: cap for the longest side; null disables downscaling entirely.
  // Defaults to 1024 (legacy behavior).
  maxDimension?: number | null;
  // JPEG quality passed to canvas.toBlob, defaults to 0.9 (legacy behavior)
  quality?: number;
}

export interface UseCameraCaptureResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  isCameraActive: boolean;
  isVideoVisible: boolean;
  isSearchingCamera: boolean;
  isCapturing: boolean;
  error: string | null;
  isTorchOn: boolean;
  isTorchSupported: boolean;
  startCamera: () => Promise<void>;
  stopCamera: () => Promise<void>;
  toggleTorch: () => Promise<void>;
  capturePhoto: (options: CapturePhotoOptions) => Promise<File>;
  processUploadedFile: (
    event: React.ChangeEvent<HTMLInputElement>,
    options: CapturePhotoOptions
  ) => Promise<File | null>;
  openGallery: () => void;
}

// Process image: crop to center square, scale to fit, or crop to an overlay element's rect
const processImage = (
  sourceImage: HTMLImageElement | HTMLVideoElement,
  canvas: HTMLCanvasElement,
  options: CapturePhotoOptions
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const maxCropSize = options.maxDimension === null ? Infinity : options.maxDimension ?? 1024;
    const jpegQuality = options.quality ?? 0.9;
    const sourceWidth =
      sourceImage instanceof HTMLVideoElement ? sourceImage.videoWidth : sourceImage.width;
    const sourceHeight =
      sourceImage instanceof HTMLVideoElement ? sourceImage.videoHeight : sourceImage.height;

    if (
      options.crop === 'element' &&
      sourceImage instanceof HTMLVideoElement &&
      options.cropElement
    ) {
      // Map the overlay element's on-screen rect onto the video source. The video is rendered
      // with object-fit: cover, so the source is uniformly scaled to fill the element's box and
      // the overflow is cropped symmetrically — invert that mapping to find the source region
      // actually visible under the overlay.
      const videoRect = sourceImage.getBoundingClientRect();
      const frameRect = options.cropElement.getBoundingClientRect();
      const coverScale = Math.max(videoRect.width / sourceWidth, videoRect.height / sourceHeight);

      if (coverScale > 0 && frameRect.width > 0 && frameRect.height > 0) {
        const overflowX = (sourceWidth * coverScale - videoRect.width) / 2;
        const overflowY = (sourceHeight * coverScale - videoRect.height) / 2;
        const sx = Math.max(0, (frameRect.left - videoRect.left + overflowX) / coverScale);
        const sy = Math.max(0, (frameRect.top - videoRect.top + overflowY) / coverScale);
        const sw = Math.min(frameRect.width / coverScale, sourceWidth - sx);
        const sh = Math.min(frameRect.height / coverScale, sourceHeight - sy);

        if (sw >= 1 && sh >= 1) {
          canvas.width = Math.round(sw);
          canvas.height = Math.round(sh);

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          ctx.drawImage(sourceImage, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(new File([blob], options.fileName, { type: 'image/jpeg' }));
              } else {
                reject(new Error('Failed to create blob from canvas'));
              }
            },
            'image/jpeg',
            jpegQuality
          );
          return;
        }
      }
      // Unusable mapping (hidden element, zero-sized rects) — fall through to 'scale' below.
    }

    if (options.crop === 'scale' || options.crop === 'element') {
      let scaledWidth = sourceWidth;
      let scaledHeight = sourceHeight;
      const maxDimension = Math.max(sourceWidth, sourceHeight);

      if (maxDimension > maxCropSize) {
        const scale = maxCropSize / maxDimension;
        scaledWidth = Math.round(sourceWidth * scale);
        scaledHeight = Math.round(sourceHeight * scale);
      }

      canvas.width = scaledWidth;
      canvas.height = scaledHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(sourceImage, 0, 0, scaledWidth, scaledHeight);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], options.fileName, { type: 'image/jpeg' });
              resolve(file);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/jpeg',
          jpegQuality
        );
      } else {
        reject(new Error('Failed to get canvas context'));
      }
      return;
    }

    // Step 1: Scale down if image is larger than maxCropSize
    let scaledWidth = sourceWidth;
    let scaledHeight = sourceHeight;
    const maxDimension = Math.max(sourceWidth, sourceHeight);

    if (maxDimension > maxCropSize) {
      // Scale proportionally so the longest side = maxCropSize
      const scale = maxCropSize / maxDimension;
      scaledWidth = Math.round(sourceWidth * scale);
      scaledHeight = Math.round(sourceHeight * scale);
    }

    // Step 2: Determine the crop size (square from center)
    const cropSize = Math.min(scaledWidth, scaledHeight);

    // Calculate center coordinates of scaled image
    const centerX = scaledWidth / 2;
    const centerY = scaledHeight / 2;

    // Calculate crop coordinates (top-left corner of the crop area)
    const cropX = Math.max(0, centerX - cropSize / 2);
    const cropY = Math.max(0, centerY - cropSize / 2);

    // Set canvas size to crop size
    canvas.width = cropSize;
    canvas.height = cropSize;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      // If we need to scale, first draw scaled image to a temporary canvas
      if (maxDimension > maxCropSize) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = scaledWidth;
        tempCanvas.height = scaledHeight;
        const tempCtx = tempCanvas.getContext('2d');

        if (tempCtx) {
          // Draw scaled image to temp canvas
          tempCtx.drawImage(sourceImage, 0, 0, scaledWidth, scaledHeight);

          // Then crop from temp canvas to final canvas
          ctx.drawImage(
            tempCanvas,
            cropX, // source x
            cropY, // source y
            cropSize, // source width
            cropSize, // source height
            0, // destination x
            0, // destination y
            cropSize, // destination width
            cropSize // destination height
          );
        }
      } else {
        // No scaling needed, crop directly from source
        const sourceCropX = Math.max(0, sourceWidth / 2 - cropSize / 2);
        const sourceCropY = Math.max(0, sourceHeight / 2 - cropSize / 2);

        ctx.drawImage(
          sourceImage,
          sourceCropX, // source x
          sourceCropY, // source y
          cropSize, // source width
          cropSize, // source height
          0, // destination x
          0, // destination y
          cropSize, // destination width
          cropSize // destination height
        );
      }

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], options.fileName, { type: 'image/jpeg' });
            resolve(file);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        'image/jpeg',
        jpegQuality
      );
    } else {
      reject(new Error('Failed to get canvas context'));
    }
  });
};

export const useCameraCapture = (): UseCameraCaptureResult => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [isTorchSupported, setIsTorchSupported] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [isSearchingCamera, setIsSearchingCamera] = useState(false);

  // Start camera with best available (torch support preferred)
  const startCamera = useCallback(async () => {
    try {
      setError(null);
      setIsSearchingCamera(true);

      let cameraIdToUse = selectedCameraId;

      // If we already have a selected camera, use it directly
      if (!cameraIdToUse) {
        console.log('No camera selected yet, starting default camera first...');
        try {
          const betterCameraId = await findCameraWithTorch();

          if (betterCameraId) {
            cameraIdToUse = betterCameraId;
            setSelectedCameraId(cameraIdToUse);
          } else {
            // ios return '' for betterCameraId, so we need to use default camera
            // Start default camera (back camera)
            const initialConstraints: MediaStreamConstraints = {
              video: {
                facingMode: 'environment',
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
            };

            const initialStream = await navigator.mediaDevices.getUserMedia(initialConstraints);
            const initialTrack = initialStream.getVideoTracks()[0];
            const initialDeviceId = initialTrack.getSettings().deviceId;

            // Release the discovery stream immediately — it exists only to learn the device
            // id. Left running, it keeps the device camera busy even after stopCamera(),
            // which only stops the stream opened below.
            initialStream.getTracks().forEach((track) => track.stop());

            cameraIdToUse = initialDeviceId || '';
            setSelectedCameraId(cameraIdToUse);
          }
        } catch (permissionErr) {
          console.error('Permission denied or camera not available:', permissionErr);
          throw permissionErr;
        }
      }

      if (!cameraIdToUse) {
        setError('No camera available');
        setIsSearchingCamera(false);
        return;
      }

      console.log(`Starting camera: ${cameraIdToUse}`);

      // Request camera access with the selected camera
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: { exact: cameraIdToUse },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      // Defensive: never hold two streams — a repeated startCamera() without a prior
      // stopCamera() would otherwise orphan the previous stream and keep the camera busy.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      // Check if torch is supported
      const videoTrack = stream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities() as MediaTrackCapabilities & {
        torch?: boolean;
      };

      setIsTorchSupported(!!capabilities.torch);
      setIsCameraActive(true);
      // hack to avoid flickering image on iOS
      setTimeout(() => {
        setIsVideoVisible(true);
      }, 100);
      setIsSearchingCamera(false);

      console.log(
        `✓ Camera active: ${videoTrack.label}`,
        capabilities.torch ? '(with torch)' : '(no torch)'
      );
    } catch (err) {
      console.error('Error accessing camera:', err);
      const error = err as Error;
      setError(`Unable to access camera: ${error.message}`);
      setIsCameraActive(false);
      setIsVideoVisible(false);
      setIsSearchingCamera(false);
    }
  }, [selectedCameraId]);

  // Stop camera
  const stopCamera = useCallback(async () => {
    try {
      // First, turn off torch if it's on
      if (isTorchOn && streamRef.current) {
        try {
          const videoTrack = streamRef.current.getVideoTracks()[0];
          if (videoTrack) {
            await videoTrack.applyConstraints({
              // @ts-expect-error - torch is not in TypeScript definitions yet
              advanced: [{ torch: false }],
            });
            console.log('Torch turned off before stopping camera');
            // Give browser time to actually turn off the torch
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        } catch (torchErr) {
          console.error('Error turning off torch:', torchErr);
        }
      }

      // Clear video element first - stop rendering
      if (videoRef.current) {
        try {
          videoRef.current.pause();
          videoRef.current.srcObject = null;
        } catch (videoErr) {
          console.error('Error clearing video element:', videoErr);
        }
      }

      if (streamRef.current) {
        try {
          const tracks = streamRef.current.getTracks();
          console.log('Camera state before stop: tracks count:', tracks.length);

          // Stop all tracks only if they exist
          if (tracks.length > 0) {
            tracks.forEach((track) => {
              console.log(
                'Stopping track:',
                track.kind,
                track.label,
                'readyState:',
                track.readyState
              );
              track.stop();
            });
            console.log('All camera tracks stopped');

            // Give the browser time to release camera resources
            // This is important for proper cleanup
            await new Promise((resolve) => setTimeout(resolve, 200));

            console.log('Camera resources should be released now');
          } else {
            console.log('Camera not running, no tracks to stop');
          }

          // Clear stream reference
          streamRef.current = null;
        } catch (err) {
          console.error('Error stopping camera tracks:', err);
          // Try to force clear even if there was an error
          try {
            streamRef.current = null;
          } catch (clearErr) {
            console.error('Error clearing stream reference:', clearErr);
          }
        }
      }

      setIsCameraActive(false);
      setIsVideoVisible(false);
      setIsTorchOn(false);
    } catch (err) {
      console.error('Error in stopCamera:', err);
    }
  }, [isTorchOn]);

  // Toggle torch/flash
  const toggleTorch = async () => {
    if (!streamRef.current) return;

    try {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      await videoTrack.applyConstraints({
        // @ts-expect-error - torch is not in TypeScript definitions yet
        advanced: [{ torch: !isTorchOn }],
      });
      setIsTorchOn(!isTorchOn);
    } catch (err) {
      console.error('Error toggling torch:', err);
    }
  };

  const capturePhoto = async (options: CapturePhotoOptions): Promise<File> => {
    if (!videoRef.current || !canvasRef.current) {
      throw new Error('Camera not ready');
    }
    setIsCapturing(true);
    setError(null);
    try {
      return await processImage(videoRef.current, canvasRef.current, options);
    } catch (err) {
      setError('Error capturing image. Please try again.');
      throw err;
    } finally {
      setIsCapturing(false);
    }
  };

  const processUploadedFile = (
    event: React.ChangeEvent<HTMLInputElement>,
    options: CapturePhotoOptions
  ): Promise<File | null> => {
    return new Promise((resolve) => {
      const file = event.target.files?.[0];
      if (!file || !canvasRef.current || isCapturing) {
        resolve(null);
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        resolve(null);
        return;
      }

      try {
        setIsCapturing(true);
        setError(null);

        // Create image element to load the file
        const img = new Image();
        const canvas = canvasRef.current;

        img.onload = async () => {
          try {
            const processedFile = await processImage(img, canvas, options);
            resolve(processedFile);
          } catch (error) {
            console.error('Error processing uploaded image:', error);
            setError('Error processing image. Please try again.');
            setIsCapturing(false);
            resolve(null);
          } finally {
            // Reset file input
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }
        };

        img.onerror = () => {
          setError('Failed to load image. Please try another file.');
          setIsCapturing(false);
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          resolve(null);
        };

        // Load the selected file
        img.src = URL.createObjectURL(file);
      } catch (err) {
        console.error('Error uploading file:', err);
        setError('Error uploading file. Please try again.');
        setIsCapturing(false);
        resolve(null);
      } finally {
        setIsCapturing(false);
      }
    });
  };

  const openGallery = () => {
    fileInputRef.current?.click();
  };

  return {
    videoRef,
    canvasRef,
    fileInputRef,
    isCameraActive,
    isVideoVisible,
    isSearchingCamera,
    isCapturing,
    error,
    isTorchOn,
    isTorchSupported,
    startCamera,
    stopCamera,
    toggleTorch,
    capturePhoto,
    processUploadedFile,
    openGallery,
  };
};
